import { Tooltip } from 'antd'
import clsx from 'clsx'
import { type FC, type ReactNode } from 'react'
import {
  FaBoxes,
  FaClipboardList,
  FaPallet,
  FaWeightHanging
} from 'react-icons/fa'

import { NumberFormatter, Spinner, UnitLabel } from '@/components/elements'
import { LABELS, STATUSES } from '@/constants/common/message.constants'
import { CargoWeightFormatter } from '@/features/shared/components'
import { PackageCountFormatter } from '@/features/shipment-order-details/components'
import { type ShipmentOrdersSummaryExEntity } from '@/libs/api'

type Props = {
  /** サマリー API の応答。取得前・失敗時は undefined */
  data: ShipmentOrdersSummaryExEntity | undefined
  /** そのキーで初めて取得している間（値が無い） */
  isLoading: boolean
  /** 取り直している間（前の値は残っている）。一括操作や再検索のあと */
  isFetching?: boolean
  isError: boolean
}

/** ロード中・失敗時の表示。CountSummary の「--」と同じ書式 */
const PLACEHOLDER = '--'

/**
 * 検索結果全体（ページをまたいだ全件）の件数・荷姿数・数量・重量（#5285 2.1）。
 *
 * 件数もこのサマリーが持つ。合計の値が 1 か所にまとまっていないと読み手が
 * 「合計」の一部を取りこぼすため、一覧の TableActions からは showTotal を外し、
 * 既存の「合計: N件」は描かない。4 値はいずれも summary API の同じ応答から出るので、
 * 一覧の total と混ぜた場合に取り直しの数秒間だけ違う断面が並ぶ問題も起きない。
 *
 * 荷姿数は荷姿カテゴリ別で、並びは API の応答順（カテゴリ ID の昇順、未設定は末尾）に従う。
 * 値は「項目 → 数」の順に置く（アイコン → 数、荷姿カテゴリ名 → 数）。外側がアイコンから
 * 始まるので、内側の荷姿カテゴリも名称から始めて階層をそろえる。
 *
 * 見た目の階層は選択行サマリー（ShipmentOrdersSelectionSummary）に合わせる:
 * 項目はアイコン、数は太字、単位（荷姿カテゴリ名・件・重量の単位）は細字。
 * アイコンは文字のラベルを持たないので、ホバーで項目名を出す。
 * 選択行サマリー側は数が先に来るままなので、並びの追従は #5759 で行う
 */
export const ShipmentOrdersSearchSummary: FC<Props> = ({
  data,
  isLoading,
  isFetching = false,
  isError
}) => {
  const isReady = !isLoading && !isError && data !== undefined
  // 取り直し中は前の値を薄く出す。一覧の行が先に新しくなる数秒間、古い値を確定値のように見せない
  const isRefreshing = isReady && isFetching

  const item = (
    testId: string,
    label: string,
    icon: ReactNode,
    value: ReactNode
  ) => (
    <span
      className="flex items-center gap-1 whitespace-nowrap"
      data-testid={testId}
    >
      <Tooltip title={label}>
        <span className="flex items-center" aria-label={label} role="img">
          {icon}
        </span>
      </Tooltip>
      <span className="text-sm font-bold">{value}</span>
    </span>
  )

  const packageCounts = () => {
    if (!isReady) return PLACEHOLDER
    if (data.packageCounts.length === 0) return '-'
    return (
      <span className="flex items-center gap-1">
        {data.packageCounts.map((row) => (
          <span
            key={row.packageCategoryId ?? 'unset'}
            className="flex items-center gap-0.5"
            data-testid="search-summary-package-category"
          >
            <UnitLabel
              className="font-normal leading-none"
              data-testid="search-summary-package-category-name"
            >
              {row.name ?? STATUSES.UNSET}
            </UnitLabel>
            {/*
              荷姿カテゴリ名は任意の文字列を取れる（「Package A」「10kg箱」など空白や
              数字を含みうる）。NumberFormatter は素のテキストを返すので、目印が無いと
              E2E は名称と数が地続きの文字列を正規表現で割るしかなく、境目を誤る。
              名称と数の双方に目印を置き、DOM の境界で読めるようにする
            */}
            <span data-testid="search-summary-package-category-count">
              <PackageCountFormatter value={row.count} />
            </span>
          </span>
        ))}
      </span>
    )
  }

  return (
    <div
      className={clsx(
        'flex flex-wrap items-center gap-x-3 gap-y-1 text-black',
        isRefreshing && 'opacity-50'
      )}
      aria-live="polite"
      aria-busy={isLoading || isFetching}
      data-testid="shipment-orders-search-summary"
    >
      {(isLoading || isRefreshing) && <Spinner size="x-small" />}
      {item(
        'search-summary-count',
        LABELS.COUNT,
        <FaClipboardList size={12} />,
        isReady ? (
          <>
            <NumberFormatter value={data.count} />
            <UnitLabel className="font-normal leading-none">
              {LABELS.COUNT_UNIT}
            </UnitLabel>
          </>
        ) : (
          PLACEHOLDER
        )
      )}
      {item(
        'search-summary-package-count',
        LABELS.PACKAGE_QUANTITY,
        <FaPallet size={12} />,
        packageCounts()
      )}
      {item(
        'search-summary-quantity',
        LABELS.QUANTITY,
        <FaBoxes size={12} />,
        isReady ? <NumberFormatter value={data.quantity} /> : PLACEHOLDER
      )}
      {item(
        'search-summary-weight',
        LABELS.WEIGHT,
        <FaWeightHanging size={12} />,
        isReady ? (
          <CargoWeightFormatter
            value={data.weight}
            unitClassName="font-normal leading-none"
          />
        ) : (
          PLACEHOLDER
        )
      )}
    </div>
  )
}
